/**
 * Whether the agent can actually reach each discovered CAD session.
 *
 * Discovery answers "is Revit open with the plugin loaded". This answers the
 * other half — can anything *talk* to it — and only the agent can, because it is
 * a separate process with its own luck against a firewall. That distinction is
 * what the Plugins pane means by a session "running successfully".
 *
 * Re-asked when the caller bumps `nonce`, so one Refresh covers the whole card.
 * Every check is bounded by the agent's own 750 ms ping, so a wedged Revit
 * delays one row rather than the pane.
 */
import { useEffect, useState } from "react";
import { checkHost, type HostReachability } from "../lib/agentHealth";
import { agentClient } from "../lib/agentClient";
import type { CadInstance } from "../bimgo";

export type Probe<T> =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; value: T };

export interface AgentHealth {
  /** Keyed by `sessionId`. Absent means not asked yet. */
  hosts: Record<string, Probe<HostReachability>>;
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function useAgentHealth(
  ready: boolean,
  instances: readonly CadInstance[],
  nonce: number,
): AgentHealth {
  const [hosts, setHosts] = useState<Record<string, Probe<HostReachability>>>({});

  // The identity of the *set* of instances, not of the array: discovery returns
  // a fresh array every poll, and keying on it would re-ping every host every
  // fifteen seconds even when nothing changed.
  const key = instances.map((i) => `${i.sessionId}@${i.host}:${i.port}`).join("|");

  useEffect(() => {
    if (!ready || instances.length === 0) {
      setHosts({});
      return;
    }

    let cancelled = false;
    setHosts(Object.fromEntries(instances.map((i) => [i.sessionId, { kind: "loading" as const }])));

    for (const instance of instances) {
      checkHost(agentClient, instance.host, instance.port)
        .then((value) => {
          if (cancelled) return;
          setHosts((current) => ({ ...current, [instance.sessionId]: { kind: "loaded", value } }));
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setHosts((current) => ({
            ...current,
            [instance.sessionId]: { kind: "error", message: message(error) },
          }));
        });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, key, nonce]);

  return { hosts };
}
